/**
 * 📢 MARKETING MESSAGES DASHBOARD - Complete message management system
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Menu,
  Stack,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  Send,
  TrendingUp,
  Campaign,
  Analytics,
  Person,
  LocationOn,
  CalendarToday,
  MoreVert,
  Refresh,
  Science,
  TrendingDown,
  ShowChart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useTheme } from '@mui/material/styles';

import { marketingApiService, type MarketingMessage as BaseMarketingMessage, type CreateMessageRequest } from '@/services/marketingApiService';

type TabType = 'messages' | 'analytics' | 'templates';
type StatusFilter = 'all' | 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
type TypeFilter = 'all' | 'announcement' | 'promotion' | 'educational' | 'notification';

// Extended interface that includes fields added by mapApiMessage
type MarketingMessage = BaseMarketingMessage & {
  content: string;
  messageType: 'announcement' | 'promotion' | 'educational' | 'notification';
  targetRole: 'renter' | 'host' | 'driver' | 'all';
  targetLocation?: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  priority: 'low' | 'medium' | 'high';
  scheduledFor?: string;
  isAbTest?: boolean;
  abTestVariant?: 'A' | 'B';
  analytics: {
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: string;
    clickThroughRate: number;
    conversionRate: number;
  };
};

interface MessageFormData {
  id?: string;
  title: string;
  content: string; // We'll map this to description
  messageType: 'announcement' | 'promotion' | 'educational' | 'notification';
  targetRole: 'renter' | 'host' | 'driver' | 'all';
  targetLocation?: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  priority: 'low' | 'medium' | 'high';
  scheduledFor?: string;
  isAbTest?: boolean;
  abTestVariant?: 'A' | 'B';
  callToActionText: string;
  callToActionRoute: string;
  icon?: string;
}

// Helper function to map API response to our expected format
const mapApiMessage = (apiMessage: any) => {
  const mapped = {
    ...apiMessage,
    content: apiMessage.description,
    messageType: apiMessage.type || 'announcement',
    targetRole: apiMessage.targeting?.userRoles?.[0] || 'all',
    targetLocation: apiMessage.targeting?.locations?.[0],
    status: apiMessage.status || 'draft', // Use API status or default to 'draft'
    priority: apiMessage.metadata?.priority === 1 ? 'high' :
              apiMessage.metadata?.priority === 2 ? 'medium' : 'low',
    scheduledFor: apiMessage.schedule?.startDate,
    isAbTest: apiMessage.metadata?.variant !== 'A',
    abTestVariant: apiMessage.metadata?.variant,
    analytics: {
      impressions: apiMessage.analytics?.impressions || 0,
      clicks: apiMessage.analytics?.clicks || 0,
      conversions: apiMessage.analytics?.conversions || 0,
      clickThroughRate: parseFloat(apiMessage.analytics?.ctr || '0') / 100,
      conversionRate: 0
    }
  };

  return mapped;
};

export default function MarketingMessagesPage() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('messages');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState<MarketingMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<MarketingMessage | null>(null);
  
  // Menu states
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Form state
  const [formData, setFormData] = useState<MessageFormData>({
    title: '',
    content: '',
    messageType: 'announcement',
    targetRole: 'all',
    status: 'draft',
    priority: 'medium',
    callToActionText: '',
    callToActionRoute: '',
    icon: ''
  });

  const [analytics, setAnalytics] = useState({
    totalMessages: 0,
    activeMessages: 0,
    totalImpressions: 0,
    totalClicks: 0,
    totalConversions: 0,
    averageCTR: 0,
    averageConversionRate: 0
  });

  // Analytics date range
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  useEffect(() => {
    fetchMessages();
    // fetchAnalytics(); // Commented out - we calculate analytics from messages instead
  }, [statusFilter, typeFilter, searchQuery]);

  const fetchMessages = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await marketingApiService.getMessages({
        status: statusFilter === 'all' ? undefined : statusFilter,
        messageType: typeFilter === 'all' ? undefined : typeFilter,
        search: searchQuery || undefined
      });

      if (response.success && response.data) {
        const mappedMessages = (response.data.messages || []).map(mapApiMessage);
        setMessages(mappedMessages);

        // Calculate analytics from messages as fallback
        const calculatedAnalytics = calculateAnalyticsFromMessages(mappedMessages);
        setAnalytics(calculatedAnalytics);
      } else {
        setError('Failed to load messages');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate analytics from messages if API doesn't provide them
  const calculateAnalyticsFromMessages = (msgs: MarketingMessage[]) => {
    const totalImpressions = msgs.reduce((sum, msg) => sum + (msg.analytics?.impressions || 0), 0);
    const totalClicks = msgs.reduce((sum, msg) => sum + (msg.analytics?.clicks || 0), 0);
    const totalConversions = msgs.reduce((sum, msg) => sum + (msg.analytics?.conversions || 0), 0);
    const avgCTR = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const avgConversionRate = totalClicks > 0 ? totalConversions / totalClicks : 0;

    return {
      totalMessages: msgs.length,
      activeMessages: msgs.filter(m => m.status === 'active').length,
      totalImpressions,
      totalClicks,
      totalConversions,
      averageCTR: avgCTR,
      averageConversionRate: avgConversionRate
    };
  };

  const handleCreateMessage = async () => {
    // Validate required fields
    if (!formData.title || !formData.content || !formData.callToActionText || !formData.callToActionRoute) {
      setError('Please fill in all required fields: Title, Content, Call-to-Action Text, and Call-to-Action Route');
      return;
    }
    
    try {
      
      // Transform form data to match backend API structure
      const apiData: CreateMessageRequest = {
        title: formData.title,
        description: formData.content, // Map content to description
        type: 'card', // Default type
        status: formData.status,
        targeting: {
          userRoles: formData.targetRole === 'all' ? ['renter', 'host', 'driver'] : [formData.targetRole],
          locations: formData.targetLocation ? [formData.targetLocation] : []
        },
        schedule: formData.scheduledFor ? {
          startDate: formData.scheduledFor
        } : undefined,
        metadata: {
          priority: formData.priority === 'high' ? 1 : formData.priority === 'medium' ? 2 : 3,
          variant: formData.isAbTest ? (formData.abTestVariant || 'A') : 'A'
        },
        callToAction: {
          text: formData.callToActionText,
          route: formData.callToActionRoute
        },
        icon: formData.icon || '💰'
      };
      
      const response = await marketingApiService.createMessage(apiData);
      if (response.success) {
        setCreateDialogOpen(false);
        resetForm();
        await fetchMessages();
        // await fetchAnalytics(); // Removed - analytics are calculated from messages
      } else {
        setError(response.error || 'Failed to create message');
      }
    } catch (error) {
      console.error('Error creating message:', error);
      setError('Failed to create message');
    }
  };

  const handleUpdateMessage = async () => {
    
    if (!selectedMessage) {
      setError('No message selected for update');
      return;
    }
    
    // Validate required fields (same as create)
    if (!formData.title || !formData.content || !formData.callToActionText || !formData.callToActionRoute) {
      setError('Please fill in all required fields: Title, Content, Call-to-Action Text, and Call-to-Action Route');
      return;
    }
    
    try {
      
      // Transform form data to match backend API structure (same as create)
      const apiData: CreateMessageRequest = {
        title: formData.title,
        description: formData.content, // Map content to description
        type: 'card', // Default type
        status: formData.status,
        targeting: {
          userRoles: formData.targetRole === 'all' ? ['renter', 'host', 'driver'] : [formData.targetRole],
          locations: formData.targetLocation ? [formData.targetLocation] : []
        },
        schedule: formData.scheduledFor ? {
          startDate: formData.scheduledFor
        } : undefined,
        metadata: {
          priority: formData.priority === 'high' ? 1 : formData.priority === 'medium' ? 2 : 3,
          variant: formData.isAbTest ? (formData.abTestVariant || 'A') : 'A'
        },
        callToAction: {
          text: formData.callToActionText,
          route: formData.callToActionRoute
        },
        icon: formData.icon || '💰'
      };
      
      const response = await marketingApiService.updateMessage(selectedMessage.id, apiData);
      
      if (response.success) {
        setEditDialogOpen(false);
        resetForm();
        
        // Add a delay to ensure backend has processed the update
        setTimeout(async () => {
          await fetchMessages();
          // await fetchAnalytics(); // Removed - analytics are calculated from messages
        }, 500);
      } else {
        setError(response.error || 'Failed to update message');
      }
    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      const response = await marketingApiService.deleteMessage(selectedMessage.id);
      if (response.success) {
        setDeleteDialogOpen(false);
        setSelectedMessage(null);
        await fetchMessages();
        // await fetchAnalytics(); // Removed - analytics are calculated from messages
      } else {
        setError(response.error || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      messageType: 'announcement',
      targetRole: 'all',
      status: 'draft',
      priority: 'medium',
      callToActionText: '',
      callToActionRoute: '',
      icon: ''
    });
    setSelectedMessage(null);
  };

  const openEditDialog = (message: any) => {
    setSelectedMessage(message);


    setFormData({
      title: message.title || '',
      content: message.content || message.description || '', // Handle both field names
      messageType: message.messageType || message.type || 'announcement',
      targetRole: message.targetRole || (message.targeting?.userRoles?.[0]) || 'all',
      targetLocation: message.targetLocation || (message.targeting?.locations?.[0]) || '',
      status: message.status || 'draft',
      priority: message.priority || (
        message.metadata?.priority === 1 ? 'high' :
        message.metadata?.priority === 2 ? 'medium' :
        message.metadata?.priority === 3 ? 'low' : 'medium'
      ),
      scheduledFor: message.scheduledFor || message.schedule?.startDate || '',
      isAbTest: message.isAbTest || (message.metadata?.variant !== 'A') || false,
      abTestVariant: message.abTestVariant || message.metadata?.variant || 'A',
      // Handle both nested and flat CTA structure
      callToActionText: message.callToAction?.text || message.cta_text || '',
      callToActionRoute: message.callToAction?.route || message.cta_route || '',
      icon: message.icon || ''
    });
    setEditDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'scheduled': return 'info';
      case 'paused': return 'warning';
      case 'draft': return 'default';
      case 'completed': return 'secondary';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              {typeof value === 'number' && title.includes('Rate') 
                ? `${(value * 100).toFixed(1)}%` 
                : value?.toLocaleString() || '0'
              }
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            width: 56, 
            height: 56, 
            borderRadius: '50%', 
            bgcolor: color, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Marketing Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage targeted messages and campaigns for your users
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { fetchMessages(); /* fetchAnalytics() removed */ }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setCreateDialogOpen(true);
            }}
          >
            Create Message
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Messages"
            value={analytics.totalMessages}
            icon={<Campaign sx={{ color: 'white' }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Messages"
            value={analytics.activeMessages}
            icon={<Send sx={{ color: 'white' }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Impressions"
            value={analytics.totalImpressions}
            icon={<Visibility sx={{ color: 'white' }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg. CTR"
            value={analytics.averageCTR}
            icon={<TrendingUp sx={{ color: 'white' }} />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => {
              setActiveTab(newValue);
            }}>
              <Tab
                label={`Messages (${messages.length})`}
                value="messages"
                icon={<Campaign />}
                iconPosition="start"
              />
              <Tab
                label="Analytics"
                value="analytics"
                icon={<Analytics />}
                iconPosition="start"
              />
              <Tab
                label="Templates"
                value="templates"
                icon={<Edit />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {activeTab === 'messages' && (
            <>
              {/* Filters */}
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="scheduled">Scheduled</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="paused">Paused</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={typeFilter}
                        label="Type"
                        onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="announcement">Announcement</MenuItem>
                        <MenuItem value="promotion">Promotion</MenuItem>
                        <MenuItem value="educational">Educational</MenuItem>
                        <MenuItem value="notification">Notification</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              {/* Messages Table */}
              <Box sx={{ p: 3 }}>
                {messages.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Message</TableCell>
                          <TableCell>Target</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Performance</TableCell>
                          <TableCell>Scheduled</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {messages.map((message) => (
                          <TableRow key={message.id} hover>
                            <TableCell>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {message.title}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                                  <Chip
                                    label={message.messageType}
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                  <Chip
                                    label={message.priority}
                                    size="small"
                                    color={getPriorityColor(message.priority) as any}
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                  {message.isAbTest && (
                                    <Chip
                                      label={`A/B Test ${message.abTestVariant || ''}`}
                                      size="small"
                                      icon={<Science />}
                                      color="info"
                                    />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {message.content?.substring(0, 80)}...
                                </Typography>
                                {message.callToAction && (
                                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                                    🔘 {message.callToAction.text} → {message.callToAction.route}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Person sx={{ fontSize: 14 }} />
                                  <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                    {message.targetRole}
                                  </Typography>
                                </Box>
                                {message.targetLocation && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">
                                      {message.targetLocation}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={message.status}
                                size="small"
                                color={getStatusColor(message.status) as any}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              {message.analytics ? (
                                <Stack spacing={0.5}>
                                  <Typography variant="caption">
                                    {(message.analytics.impressions || 0).toLocaleString()} impressions
                                  </Typography>
                                  <Typography variant="caption">
                                    CTR: {((message.analytics.clickThroughRate || 0) * 100).toFixed(1)}%
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No data
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {message.scheduledFor ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarToday sx={{ fontSize: 14 }} />
                                  <Typography variant="caption">
                                    {new Date(message.scheduledFor).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Immediate
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setSelectedMessage(message);
                                  setActionMenuAnchor(e.currentTarget);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Campaign sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No messages found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create your first marketing message to get started
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setCreateDialogOpen(true)}
                    >
                      Create Message
                    </Button>
                  </Paper>
                )}
              </Box>
            </>
          )}

          {activeTab === 'analytics' && (
            <Box sx={{ p: 3 }}>
              {/* Analytics Header with Date Range */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Marketing Performance Analytics
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Empty State */}
              {messages.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                  <Analytics sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No Analytics Data Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create some marketing messages to see performance analytics and metrics.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setActiveTab('messages');
                      setCreateDialogOpen(true);
                    }}
                  >
                    Create First Message
                  </Button>
                </Paper>
              ) : (
                <Box>
                  {/* Charts and Analytics Content */}

                  {/* Performance Over Time Chart */}
                  <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Performance Trends
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={messages.map((msg) => ({
                      name: msg.title.substring(0, 20),
                      impressions: msg.analytics?.impressions || 0,
                      clicks: msg.analytics?.clicks || 0,
                      conversions: msg.analytics?.conversions || 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="impressions" stroke={theme.palette.primary.main} strokeWidth={2} />
                      <Line type="monotone" dataKey="clicks" stroke={theme.palette.success.main} strokeWidth={2} />
                      <Line type="monotone" dataKey="conversions" stroke={theme.palette.warning.main} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                {/* Message Performance Comparison */}
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Message Performance Comparison
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={messages.slice(0, 5).map(msg => ({
                          name: msg.title.substring(0, 15),
                          ctr: (msg.analytics?.clickThroughRate || 0) * 100,
                          impressions: msg.analytics?.impressions || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="ctr" fill={theme.palette.primary.main} name="CTR (%)" />
                          <Bar yAxisId="right" dataKey="impressions" fill={theme.palette.secondary.main} name="Impressions" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Message Type Distribution */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Message Type Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(
                              messages.reduce((acc, msg) => {
                                acc[msg.messageType] = (acc[msg.messageType] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>)
                            ).map(([type, count]) => ({
                              name: type,
                              value: count
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.keys(
                              messages.reduce((acc, msg) => {
                                acc[msg.messageType] = (acc[msg.messageType] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>)
                            ).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={[
                                theme.palette.primary.main,
                                theme.palette.secondary.main,
                                theme.palette.success.main,
                                theme.palette.warning.main,
                                theme.palette.error.main,
                                theme.palette.info.main
                              ][index % 6]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Top Performing Messages */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Top Performing Messages
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Rank</TableCell>
                              <TableCell>Message Title</TableCell>
                              <TableCell align="right">Impressions</TableCell>
                              <TableCell align="right">Clicks</TableCell>
                              <TableCell align="right">CTR</TableCell>
                              <TableCell align="right">Conversions</TableCell>
                              <TableCell>Trend</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {messages
                              .sort((a, b) => (b.analytics?.clicks || 0) - (a.analytics?.clicks || 0))
                              .slice(0, 10)
                              .map((message, index) => (
                                <TableRow key={message.id} hover>
                                  <TableCell>
                                    <Chip
                                      label={`#${index + 1}`}
                                      size="small"
                                      color={index === 0 ? 'primary' : index < 3 ? 'secondary' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {message.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {message.messageType}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {(message.analytics?.impressions || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell align="right">
                                    {(message.analytics?.clicks || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={`${((message.analytics?.clickThroughRate || 0) * 100).toFixed(2)}%`}
                                      size="small"
                                      color={
                                        (message.analytics?.clickThroughRate || 0) > 0.05 ? 'success' :
                                        (message.analytics?.clickThroughRate || 0) > 0.02 ? 'warning' : 'default'
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {(message.analytics?.conversions || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {(message.analytics?.clickThroughRate || 0) > 0.03 ? (
                                      <TrendingUp sx={{ color: 'success.main' }} />
                                    ) : (
                                      <TrendingDown sx={{ color: 'error.main' }} />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {messages.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Analytics sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No analytics data available yet. Create some messages to see performance metrics.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Conversion Funnel */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Conversion Funnel
                      </Typography>
                      <Box sx={{ p: 2 }}>
                        {[
                          { label: 'Impressions', value: analytics.totalImpressions || 0, width: 100 },
                          { label: 'Clicks', value: analytics.totalClicks || 0, width: ((analytics.totalClicks || 0) / (analytics.totalImpressions || 1) * 100) || 0 },
                          { label: 'Conversions', value: analytics.totalConversions || 0, width: ((analytics.totalConversions || 0) / (analytics.totalImpressions || 1) * 100) || 0 }
                        ].map((stage, index) => (
                          <Box key={stage.label} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {stage.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {(stage.value || 0).toLocaleString()} ({(stage.width || 0).toFixed(1)}%)
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={stage.width}
                              sx={{
                                height: 32,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: [theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main][index]
                                }
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Key Metrics Summary */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Key Metrics Summary
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Visibility color="primary" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Average Impressions per Message
                            </Typography>
                          </Box>
                          <Typography variant="h6">
                            {messages.length > 0
                              ? Math.round((analytics.totalImpressions || 0) / messages.length).toLocaleString()
                              : 0
                            }
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShowChart color="success" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Overall Click-Through Rate
                            </Typography>
                          </Box>
                          <Typography variant="h6">
                            {((analytics.averageCTR || 0) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp color="warning" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Conversion Rate
                            </Typography>
                          </Box>
                          <Typography variant="h6">
                            {((analytics.averageConversionRate || 0) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Campaign color="info" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Active Campaigns
                            </Typography>
                          </Box>
                          <Typography variant="h6">
                            {analytics.activeMessages || 0}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 'templates' && (
            <Box sx={{ p: 3 }}>
              {/* Templates Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Message Templates
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pre-built templates to quickly create marketing messages
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => {
                    // Future: Add custom template creation
                    setError('Custom template creation coming soon!');
                  }}
                >
                  Save as Template
                </Button>
              </Box>

              {/* Template Categories */}
              <Grid container spacing={3}>
                {/* Welcome Messages */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Welcome & Onboarding
                  </Typography>
                </Grid>
                {[
                  {
                    title: 'New Renter Welcome',
                    description: 'Welcome new renters and guide them through their first booking',
                    messageType: 'announcement' as const,
                    targetRole: 'renter' as const,
                    icon: '👋',
                    content: 'Welcome to PolarRide! We\'re excited to have you join our community. Get started by browsing our available vehicles and making your first booking.',
                    callToActionText: 'Browse Vehicles',
                    callToActionRoute: '/vehicles'
                  },
                  {
                    title: 'New Host Welcome',
                    description: 'Welcome new hosts and guide them through listing their first vehicle',
                    messageType: 'educational' as const,
                    targetRole: 'host' as const,
                    icon: '🚗',
                    content: 'Congratulations on becoming a PolarRide host! List your first vehicle and start earning passive income today.',
                    callToActionText: 'List Your Vehicle',
                    callToActionRoute: '/become-host'
                  },
                  {
                    title: 'Driver Onboarding',
                    description: 'Guide new drivers through the verification process',
                    messageType: 'educational' as const,
                    targetRole: 'driver' as const,
                    icon: '👨‍✈️',
                    content: 'Welcome to the PolarRide driver network! Complete your profile verification to start offering rides.',
                    callToActionText: 'Complete Verification',
                    callToActionRoute: '/driver/verification'
                  }
                ].map((template, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ height: '100%', '&:hover': { boxShadow: 6, cursor: 'pointer' } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography sx={{ fontSize: 32 }}>{template.icon}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {template.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {template.description}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip label={template.messageType} size="small" variant="outlined" />
                          <Chip label={template.targetRole} size="small" color="primary" />
                        </Stack>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setFormData({
                              title: template.title,
                              content: template.content,
                              messageType: template.messageType,
                              targetRole: template.targetRole,
                              status: 'draft',
                              priority: 'medium',
                              callToActionText: template.callToActionText,
                              callToActionRoute: template.callToActionRoute,
                              icon: template.icon
                            });
                            setCreateDialogOpen(true);
                          }}
                        >
                          Use Template
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Promotional Messages */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Promotions & Offers
                  </Typography>
                </Grid>
                {[
                  {
                    title: 'Weekend Special',
                    description: 'Promote weekend booking discounts',
                    messageType: 'promotion' as const,
                    targetRole: 'renter' as const,
                    icon: '🎉',
                    content: 'This weekend only! Get 20% off all bookings made Friday through Sunday. Limited time offer!',
                    callToActionText: 'Book Now',
                    callToActionRoute: '/vehicles'
                  },
                  {
                    title: 'Refer a Friend',
                    description: 'Encourage users to refer friends with rewards',
                    messageType: 'promotion' as const,
                    targetRole: 'all' as const,
                    icon: '💰',
                    content: 'Refer a friend and both of you get $50 credit! Share PolarRide with your network and earn rewards.',
                    callToActionText: 'Start Referring',
                    callToActionRoute: '/referrals'
                  },
                  {
                    title: 'Early Bird Discount',
                    description: 'Reward early bookings with special pricing',
                    messageType: 'promotion' as const,
                    targetRole: 'renter' as const,
                    icon: '⏰',
                    content: 'Book 7+ days in advance and save 15%! Plan ahead and save on your next trip.',
                    callToActionText: 'Browse Vehicles',
                    callToActionRoute: '/vehicles'
                  }
                ].map((template, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ height: '100%', '&:hover': { boxShadow: 6, cursor: 'pointer' } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography sx={{ fontSize: 32 }}>{template.icon}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {template.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {template.description}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip label={template.messageType} size="small" variant="outlined" />
                          <Chip label={template.targetRole} size="small" color="primary" />
                        </Stack>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setFormData({
                              title: template.title,
                              content: template.content,
                              messageType: template.messageType,
                              targetRole: template.targetRole,
                              status: 'draft',
                              priority: 'high',
                              callToActionText: template.callToActionText,
                              callToActionRoute: template.callToActionRoute,
                              icon: template.icon
                            });
                            setCreateDialogOpen(true);
                          }}
                        >
                          Use Template
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Educational Content */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Educational & Tips
                  </Typography>
                </Grid>
                {[
                  {
                    title: 'Safety Tips',
                    description: 'Share important safety information with users',
                    messageType: 'educational' as const,
                    targetRole: 'all' as const,
                    icon: '🛡️',
                    content: 'Your safety is our priority. Review our safety guidelines and best practices for a secure ride experience.',
                    callToActionText: 'Learn More',
                    callToActionRoute: '/safety'
                  },
                  {
                    title: 'Host Best Practices',
                    description: 'Help hosts maximize their earnings',
                    messageType: 'educational' as const,
                    targetRole: 'host' as const,
                    icon: '📚',
                    content: 'Increase your booking rate by 30%! Learn proven strategies from our top-performing hosts.',
                    callToActionText: 'View Guide',
                    callToActionRoute: '/host/best-practices'
                  },
                  {
                    title: 'App Features Tutorial',
                    description: 'Showcase new app features and improvements',
                    messageType: 'announcement' as const,
                    targetRole: 'all' as const,
                    icon: '✨',
                    content: 'Discover our latest features! We\'ve added new tools to make your PolarRide experience even better.',
                    callToActionText: 'Explore Features',
                    callToActionRoute: '/features'
                  }
                ].map((template, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ height: '100%', '&:hover': { boxShadow: 6, cursor: 'pointer' } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography sx={{ fontSize: 32 }}>{template.icon}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {template.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {template.description}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip label={template.messageType} size="small" variant="outlined" />
                          <Chip label={template.targetRole} size="small" color="primary" />
                        </Stack>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setFormData({
                              title: template.title,
                              content: template.content,
                              messageType: template.messageType,
                              targetRole: template.targetRole,
                              status: 'draft',
                              priority: 'medium',
                              callToActionText: template.callToActionText,
                              callToActionRoute: template.callToActionRoute,
                              icon: template.icon
                            });
                            setCreateDialogOpen(true);
                          }}
                        >
                          Use Template
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (selectedMessage) openEditDialog(selectedMessage);
          setActionMenuAnchor(null);
        }}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          setDeleteDialogOpen(true);
          setActionMenuAnchor(null);
        }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Message Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {createDialogOpen ? 'Create New Message' : 'Edit Message'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={(e) => {
            e.preventDefault();
          }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Message Type</InputLabel>
                <Select
                  value={formData.messageType}
                  label="Message Type"
                  onChange={(e) => setFormData({ ...formData, messageType: e.target.value as any })}
                >
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="promotion">Promotion</MenuItem>
                  <MenuItem value="educational">Educational</MenuItem>
                  <MenuItem value="notification">Notification</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Target Role</InputLabel>
                <Select
                  value={formData.targetRole}
                  label="Target Role"
                  onChange={(e) => setFormData({ ...formData, targetRole: e.target.value as any })}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="renter">Renters</MenuItem>
                  <MenuItem value="host">Hosts</MenuItem>
                  <MenuItem value="driver">Drivers</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Target Location (Optional)"
                value={formData.targetLocation || ''}
                onChange={(e) => setFormData({ ...formData, targetLocation: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Schedule For (Optional)"
                value={formData.scheduledFor || ''}
                onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAbTest || false}
                    onChange={(e) => setFormData({ ...formData, isAbTest: e.target.checked })}
                  />
                }
                label="Enable A/B Testing"
              />
              {formData.isAbTest && (
                <FormControl sx={{ ml: 2, minWidth: 120 }} size="small">
                  <InputLabel>Variant</InputLabel>
                  <Select
                    value={formData.abTestVariant || 'A'}
                    label="Variant"
                    onChange={(e) => setFormData({ ...formData, abTestVariant: e.target.value as any })}
                  >
                    <MenuItem value="A">Variant A</MenuItem>
                    <MenuItem value="B">Variant B</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            
            {/* Call-to-Action Fields */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Call-to-Action (Required)
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Button Text *"
                placeholder="e.g. Get Started, Learn More"
                value={formData.callToActionText}
                onChange={(e) => setFormData({ ...formData, callToActionText: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Route/URL *"
                placeholder="e.g. /become-host, /become-driver"
                value={formData.callToActionRoute}
                onChange={(e) => setFormData({ ...formData, callToActionRoute: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Icon/Emoji (Optional)"
                placeholder="e.g. 💰, 🚗, 👨‍💼"
                value={formData.icon || ''}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                helperText="Add an emoji or icon to display with your message"
              />
            </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (createDialogOpen) {
                handleCreateMessage();
              } else if (editDialogOpen) {
                handleUpdateMessage();
              }
            }}
            disabled={!formData.title || !formData.content || !formData.callToActionText || !formData.callToActionRoute}
          >
            {createDialogOpen ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedMessage?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMessage}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}